/**
 * TCG-Produktraster: tauscht das Raster unter den Startseiten-Kacheln aus,
 * ohne die Seite neu zu laden.
 *
 * Model      TcgGridModel      laedt das Raster-Markup einer Collection (Cache, Abbruch)
 * ViewModel  TcgGridViewModel  haelt Auswahl- und Ladezustand; kennt kein DOM
 * View       TcgProductGrid    Custom Element; leitet Klicks weiter und rendert den Zustand
 *
 * Das Markup liefert die Vorlage templates/collection.tcg-grid-ajax.json
 * (ohne Layout), gerendert von sections/dynamic-tcg-grid.liquid.
 */
import { Component } from '@theme/component';
import { morph } from '@theme/morph';

const GRID_VIEW = 'tcg-grid-ajax';
const GRID_SELECTOR = '[data-tcg-grid]';
const COLLECTION_PATH = /\/collections\/[^/]+\/?$/;

class TcgGridModel {
  /** @type {Map<string, Element>} */
  #cache = new Map();

  /**
   * @param {string} collectionUrl
   * @param {AbortSignal} signal
   * @returns {Promise<Element>} Kopie des Raster-Elements aus der Antwort
   */
  async fetchGrid(collectionUrl, signal) {
    const url = new URL(collectionUrl);
    url.searchParams.set('view', GRID_VIEW);
    const key = url.toString();

    if (!this.#cache.has(key)) {
      const response = await fetch(key, { signal });
      if (!response.ok) throw new Error(`TCG grid request failed: ${response.status}`);

      const html = await response.text();
      const grid = new DOMParser().parseFromString(html, 'text/html').querySelector(GRID_SELECTOR);
      if (!grid) throw new Error('TCG grid markup missing in response');

      this.#cache.set(key, grid);
    }

    return /** @type {Element} */ (this.#cache.get(key).cloneNode(true));
  }
}

/**
 * @typedef {{ status: 'idle' | 'loading' | 'ready' | 'error', url: string | null, content: Element | null }} TcgGridState
 */

class TcgGridViewModel extends EventTarget {
  #model;
  /** @type {AbortController | null} */
  #activeRequest = null;
  /** @type {TcgGridState} */
  state = Object.freeze({ status: 'idle', url: null, content: null });

  /** @param {TcgGridModel} model */
  constructor(model) {
    super();
    this.#model = model;
  }

  /** @param {string} url */
  async select(url) {
    const { status, url: currentUrl } = this.state;
    if (url === currentUrl && (status === 'loading' || status === 'ready')) return;

    this.#activeRequest?.abort();
    const request = new AbortController();
    this.#activeRequest = request;
    this.#setState({ status: 'loading', url, content: null });

    try {
      const content = await this.#model.fetchGrid(url, request.signal);
      if (!request.signal.aborted) this.#setState({ status: 'ready', url, content });
    } catch {
      if (!request.signal.aborted) this.#setState({ status: 'error', url, content: null });
    }
  }

  /** @param {TcgGridState} state */
  #setState(state) {
    this.state = Object.freeze(state);
    this.dispatchEvent(new Event('change'));
  }
}

/**
 * @typedef {{ grid: HTMLElement, status: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class TcgProductGrid extends Component {
  requiredRefs = ['grid', 'status'];

  #viewModel = new TcgGridViewModel(new TcgGridModel());
  /** @type {HTMLAnchorElement | null} */
  #pendingLink = null;

  connectedCallback() {
    super.connectedCallback();
    // Im Theme-Editor waehlen Klicks auf Kacheln Bloecke aus – dort nicht eingreifen.
    if (window.Shopify?.designMode) return;

    this.#viewModel.addEventListener('change', this.#render);
    document.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#viewModel.removeEventListener('change', this.#render);
    document.removeEventListener('click', this.#handleClick);
  }

  /** Links in den Kachel-Sections, die in data-trigger-sections stehen. */
  get #triggerLinks() {
    return (this.dataset.triggerSections ?? '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean)
      .map((key) => `.shopify-section[id$="__${CSS.escape(key)}"] a[href]`)
      .join(', ');
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const selector = this.#triggerLinks;
    if (!selector || !(event.target instanceof Element)) return;

    const link = /** @type {HTMLAnchorElement | null} */ (event.target.closest(selector));
    if (!link || link.target === '_blank') return;

    const url = new URL(link.href);
    if (url.origin !== window.location.origin || !COLLECTION_PATH.test(url.pathname)) return;

    event.preventDefault();
    this.#pendingLink = link;
    this.#viewModel.select(url.toString());
  };

  #render = () => {
    const { status, url, content } = this.#viewModel.state;
    const grid = this.refs.grid;

    grid.setAttribute('aria-busy', String(status === 'loading'));

    if (status === 'error' && url) {
      // Rueckfall: normale Navigation, damit der Klick nie ins Leere laeuft.
      window.location.assign(url);
      return;
    }

    if (status !== 'ready' || !content) return;

    // Nur die Kinder tauschen: Klassen und Styles des live gerenderten .section-Elements
    // (inkl. section-spezifischer Farbklasse) bleiben unveraendert.
    const next = /** @type {HTMLElement} */ (grid.cloneNode(false));
    next.replaceChildren(...content.childNodes);
    next.setAttribute('aria-busy', 'false');
    morph(grid, next);

    this.#markActiveLink();
    this.refs.status.textContent = grid.querySelector('h1, h2, h3, h4, h5, h6')?.textContent?.trim() ?? '';
    this.#revealIfOffscreen();
  };

  #markActiveLink() {
    for (const link of document.querySelectorAll(this.#triggerLinks)) {
      if (link === this.#pendingLink) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  }

  #revealIfOffscreen() {
    const { top } = this.refs.grid.getBoundingClientRect();
    if (top >= 0 && top <= window.innerHeight * 0.75) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.refs.grid.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('tcg-product-grid')) {
  customElements.define('tcg-product-grid', TcgProductGrid);
}
